import { __awaiter } from "tslib";
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import { ParagraphTertiary, ProgressBar, SpacerVertical } from '@klarna/bubble-ui';
import { Alert } from 'moet-core';
import { getDocumentsOfType } from '@/selectors/documentUpload';
import { AttachmentType } from '@/constants/documents';
import { logEvent } from '@/utils/analytics';
import { ACCEPTED_FORMATS, MAX_FILE_SIZE_IN_MB, MAX_NUMBER_OF_FILES, tPrefix } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { LoaderContainer, LoaderTitle } from './styledComponents';
import { UploadingError } from './UploadingError';
import { UploadArea } from './UploadArea';
const Uploader = ({ t, locale, documentsOfType, type = AttachmentType.SUPPORTING_DOCUMENT, onUploadDocument, cyElementId, errors, setErrors, }) => {
    const [loading, setLoading] = useState(false);
    const [uploadingError, setUploadingError] = useState(null);
    const [percentage, setPercentage] = useState(0);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const upload = useCallback((submittedFiles) => {
        const doUpload = (file) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const form = new FormData();
            form.append('file', file);
            setLoading(true);
            setUploadingError(null);
            if (file.size > MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
                setLoading(false);
                setUploadingError(413);
            }
            else {
                try {
                    yield onUploadDocument(form, {
                        onUploadProgress: event => {
                            const percentage = Math.round((event.loaded * 100) / event.total);
                            setPercentage(percentage);
                        },
                    });
                    logEvent(ANALYTICS_EVENTS.uploadDocument, { documentType: type });
                }
                catch (e) {
                    // API returns 413 if the file is too large
                    if (((_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.status) === 413) {
                        setUploadingError(413);
                    }
                    else {
                        setUploadingError(e);
                    }
                }
                finally {
                    setLoading(false);
                }
            }
        });
        submittedFiles.forEach(doUpload);
    }, [onUploadDocument]);
    const onUpload = useCallback((filesToUpload) => {
        setUploadingFiles(filesToUpload);
        if (filesToUpload.length > 0) {
            upload(filesToUpload);
        }
    }, [upload]);
    const reUpload = useCallback(() => {
        return upload(uploadingFiles);
    }, [uploadingFiles, upload]);
    const errorProps = { uploadingFiles, reUpload, t, uploadingError, loading };
    const UploadingProgress = () => {
        const localizedPercentage = Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(percentage / 100);
        return (React.createElement(React.Fragment, null, uploadingFiles === null || uploadingFiles === void 0 ? void 0 : uploadingFiles.map(file => (React.createElement(LoaderContainer, { key: file.lastModified },
            React.createElement(LoaderTitle, null,
                React.createElement(ParagraphTertiary, { correctFootprint: true, overflowEllipsisWidth: "calc(100% - 40px)" }, file.name),
                React.createElement(ParagraphTertiary, { correctFootprint: true }, localizedPercentage)),
            React.createElement(SpacerVertical, { extraSmall: true }),
            React.createElement(ProgressBar, { percentage: percentage }))))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(UploadArea, { cyElementId: cyElementId, documents: documentsOfType, errors: errors, onUpload: onUpload, setErrors: setErrors, t: t }),
        React.createElement(SpacerVertical, { extraSmall: true }),
        errors && errors.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(Alert, { type: "error", [errors.length > 1 ? 'listItems' : 'content']: errors.map(error => t(`${tPrefix}.errors.${error}`, {
                    number: `${MAX_NUMBER_OF_FILES}`,
                    acceptedFileFormats: ACCEPTED_FORMATS.join(' '),
                })) }),
            React.createElement(SpacerVertical, { extraSmall: true }))),
        loading && React.createElement(UploadingProgress, null),
        uploadingError && React.createElement(UploadingError, Object.assign({}, errorProps))));
};
export default connect((state, { type }) => {
    return {
        documentsOfType: getDocumentsOfType(type)(state),
        locale: i18nSelectors.locale(state),
        t: translator(state),
    };
})(Uploader);
